import React, { useState, useMemo, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Button,
  Collapse,
  Chip,
  CircularProgress,
  Link,
  Tabs,
  Tab,
  Avatar,
  Stack,
  Checkbox
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ProjectFreelancerProfile } from '../../models/ProjectFreelancerProfile';
import { zoullooColors } from '../../theme';
import FreelancerDetails from '../common/FreelancerDetails';
import { stringAvatar } from '../../utils/avatarUtils';
import { useFreelancerProfile } from '../../hooks/useFreelancerProfile';
import { useFreelancerProfileEvaluations } from '../../hooks/useProjectEvaluations';
import { EvaluatorType } from '../../models/ProjectEvaluation';
import FreelancerProposalDetails from '../../pages/project/components/FreelancerProposalDetails';
import { FreelancerProposalStatus } from '../../models/ProposalWorkflow';
import { useBulkTransmitToClient } from '../../hooks/useProjectFreelancerProfiles';
import StarRating from './evaluation/StarRating';

interface ProjectFreelancersTableProps {
  projectId: number;
  freelancers: ProjectFreelancerProfile[];
  isLoading: boolean;
  feePercentage: number;
  externalCostsFeePercentage?: number;
  expensesFeePercentage?: number;
  processingFees?: number;
  onAddFreelancer: () => void;
  onViewFreelancerDetails?: (freelancerKey: string) => void;
  isClientView?: boolean;
  hasAcceptedProposal?: boolean;
}

// Component to load and display freelancer avatar with photo
const FreelancerAvatar: React.FC<{
  freelancerProfileId: number;
  freelancerName: string;
}> = ({ freelancerProfileId, freelancerName }) => {
  const { data: freelancerProfile } = useFreelancerProfile(freelancerProfileId, true);

  const nameParts = freelancerName?.split(' ') || [];
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  // Build photo URL if available
  const photoUrl = freelancerProfile?.freelancer?.picture?.urlPath
    ? `${process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL}/${process.env.REACT_APP_FREELANCER_PICTURES_CONTAINER_NAME}/${freelancerProfile.freelancer.picture.urlPath}`
    : undefined;

  return (
    <Avatar
      {...stringAvatar(firstName, lastName)}
      sx={{
        width: 40,
        height: 40,
        fontSize: '1rem',
        bgcolor: zoullooColors.secondary.light
      }}
      src={photoUrl}
    />
  );
};

// Component to load and display freelancer expertise and experience
const FreelancerExpertiseExperience: React.FC<{
  freelancerProfileId: number;
}> = ({ freelancerProfileId }) => {
  const { t } = useTranslation();
  const { data: freelancerProfile } = useFreelancerProfile(freelancerProfileId, true);

  if (!freelancerProfile) {
    return <Typography variant="caption" color="textSecondary">-</Typography>;
  }

  const expertiseLabel = freelancerProfile.expertise?.label
    ? t(`Expertises.${freelancerProfile.expertise.label}`)
    : '';

  const experienceLabel = freelancerProfile.experience?.label
    ? t(`Experiences.${freelancerProfile.experience.label}`)
    : '';

  if (!expertiseLabel && !experienceLabel) {
    return <Typography variant="caption" color="textSecondary">-</Typography>;
  }

  return (
    <Typography variant="caption" color="textSecondary">
      {expertiseLabel}{expertiseLabel && experienceLabel ? ' - ' : ''}{experienceLabel}
    </Typography>
  );
};

// Component to load and display freelancer evaluation rating
const FreelancerRating: React.FC<{
  freelancerProfileId: number;
}> = ({ freelancerProfileId }) => {
  const { data: evaluationSummary } = useFreelancerProfileEvaluations(freelancerProfileId, freelancerProfileId > 0, EvaluatorType.Client);

  if (!evaluationSummary?.averageRating || evaluationSummary.totalCount === 0) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <StarRating value={evaluationSummary.averageRating} readOnly size="small" />
      <Typography variant="caption" color="text.secondary">
        ({evaluationSummary.totalCount})
      </Typography>
    </Box>
  );
};

const ProjectFreelancersTable: React.FC<ProjectFreelancersTableProps> = ({
  projectId,
  freelancers,
  isLoading,
  feePercentage,
  externalCostsFeePercentage = 0,
  expensesFeePercentage = 0,
  processingFees = 0,
  onAddFreelancer,
  isClientView = false,
  hasAcceptedProposal = false,
}) => {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState<{ [key: number]: number }>({});
  const [selectedProposalIds, setSelectedProposalIds] = useState<number[]>([]);
  const bulkTransmitMutation = useBulkTransmitToClient();

  // Eligible statuses for bulk transmit
  const isEligibleForBulkTransmit = useCallback((status: string) => {
    return status === FreelancerProposalStatus.SubmittedCSM || status === FreelancerProposalStatus.RevisedForCSM;
  }, []);

  // Get all eligible freelancers (those with proposals that can be transmitted)
  const eligibleFreelancers = useMemo(() => {
    return freelancers.filter(f => f.proposal?.id && isEligibleForBulkTransmit(f.status));
  }, [freelancers, isEligibleForBulkTransmit]);

  const handleSelectProposal = (proposalId: number) => {
    setSelectedProposalIds(prev =>
      prev.includes(proposalId)
        ? prev.filter(id => id !== proposalId)
        : [...prev, proposalId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProposalIds.length === eligibleFreelancers.length) {
      setSelectedProposalIds([]);
    } else {
      setSelectedProposalIds(eligibleFreelancers.map(f => f.proposal!.id));
    }
  };

  const handleBulkTransmit = async () => {
    if (selectedProposalIds.length === 0 || bulkTransmitMutation.isPending) return;
    const ids = [...selectedProposalIds];
    setSelectedProposalIds([]);
    await bulkTransmitMutation.mutateAsync(ids);
  };

  // Calculate grand total including Zoulloo fees, external costs, expenses (with their fees), and processing fees
  const calculateGrandTotal = (proposal: ProjectFreelancerProfile['proposal']) => {
    if (!proposal?.totalBudget) return null;

    const totalBudget = proposal.totalBudget || 0;

    // Use reverse margin formula: Total = Budget / (1 - feePercentage/100)
    // This matches the calculation in FreelancerProposalDetails
    const totalWithZoullooFees = feePercentage >= 100 || feePercentage < 0
      ? totalBudget
      : totalBudget / (1 - feePercentage / 100);

    const externalCosts = proposal.totalExternalCosts || 0;
    const externalCostsWithFees = externalCosts * (1 + externalCostsFeePercentage / 100);
    const expenses = proposal.totalExpenses || 0;
    const expensesWithFees = expenses * (1 + expensesFeePercentage / 100);

    return totalWithZoullooFees + externalCostsWithFees + expensesWithFees + (processingFees || 0);
  };

  const handleExpandRow = (freelancerId: number, tabIndex: number) => {
    const newExpanded = new Set(expandedRows);

    if (expandedRows.has(freelancerId) && activeTab[freelancerId] === tabIndex) {
      // If same tab is clicked, close the row
      newExpanded.delete(freelancerId);
    } else {
      // Open the row and set the active tab
      newExpanded.add(freelancerId);
      setActiveTab({ ...activeTab, [freelancerId]: tabIndex });
    }

    setExpandedRows(newExpanded);
  };

  const handleTabChange = (freelancerId: number, newValue: number) => {
    setActiveTab({ ...activeTab, [freelancerId]: newValue });
  };

  const getStatusStyle = () => {
    const textColor = zoullooColors.secondary.main;

    return {
      backgroundColor: zoullooColors.secondary.light + '20', 
      color: textColor,
      fontWeight: 500
    };
  };

  const getStatusLabel = (status: string): string => {
    return t(`enums.projectFreelancerProfileStatus.${status}`) || status;
  };

  const renderTableContent = () => {
    const colCount = !isClientView ? 6 : 5;

    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={colCount} align="center">
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    if (freelancers.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={colCount} align="center">
            <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
              {t('project.noTalentsAssociated')}
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return freelancers.map((freelancer) => {
      return (
        <React.Fragment key={freelancer.id}>
          <TableRow hover>
            {!isClientView && (
              <TableCell padding="checkbox" sx={{ width: 40, pl: 1.5, pr: 0 }}>
                {freelancer.proposal?.id && isEligibleForBulkTransmit(freelancer.status) ? (
                  <Checkbox
                    checked={selectedProposalIds.includes(freelancer.proposal.id)}
                    onChange={() => handleSelectProposal(freelancer.proposal!.id)}
                    size="small"
                    sx={{
                      color: zoullooColors.secondary.main,
                      '&.Mui-checked': { color: zoullooColors.secondary.main },
                    }}
                  />
                ) : (
                  <Checkbox disabled size="small" />
                )}
              </TableCell>
            )}
            <TableCell>
              <Stack direction="row" spacing={2} alignItems="center">
                <FreelancerAvatar
                  freelancerProfileId={freelancer.freelancerProfileId}
                  freelancerName={freelancer.freelancerName}
                />
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {freelancer.freelancerName}
                  </Typography>
                  <FreelancerExpertiseExperience
                    freelancerProfileId={freelancer.freelancerProfileId}
                  />
                  <FreelancerRating
                    freelancerProfileId={freelancer.freelancerProfileId}
                  />
                </Box>
              </Stack>
            </TableCell>
          <TableCell>
            <Link
              component="button"
              variant="body2"
              onClick={() => handleExpandRow(freelancer.id, 0)}
              sx={{ textDecoration: 'none' }}
            >
              {expandedRows.has(freelancer.id) && activeTab[freelancer.id] === 0 ? t('projectContacts.hide') : t('projectContacts.show')}
            </Link>
          </TableCell>
          <TableCell>
            <Link
              component="button"
              variant="body2"
              onClick={() => handleExpandRow(freelancer.id, 1)}
              sx={{ textDecoration: 'none' }}
            >
              {expandedRows.has(freelancer.id) && activeTab[freelancer.id] === 1 ? t('projectContacts.hide') : t('projectContacts.show')}
            </Link>
          </TableCell>
          <TableCell align="right">
            <Typography variant="body2">
              {calculateGrandTotal(freelancer.proposal) !== null
                ? new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'EUR',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(calculateGrandTotal(freelancer.proposal)!)
                : '-'}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Chip
              label={getStatusLabel(freelancer.status)}
              size="small"
              sx={getStatusStyle()}
            />
          </TableCell>
        </TableRow>
        {/* Expandable Row with Tabs */}
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={colCount}>
            <Collapse in={expandedRows.has(freelancer.id)} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                <Tabs 
                  value={activeTab[freelancer.id] || 0}
                  onChange={(_, newValue) => handleTabChange(freelancer.id, newValue)}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    mb: 2,
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      minHeight: 40,
                      color: zoullooColors.text.secondary,
                      '&.Mui-selected': {
                        color: zoullooColors.secondary.main,
                      },
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: zoullooColors.secondary.main,
                    },
                  }}
                >
                  <Tab label={t('project.profile')} />
                  <Tab label={t('project.proposalColumn')} />
                </Tabs>
                
                {/* Profile Tab Content */}
                {activeTab[freelancer.id] === 0 && (
                  <Box sx={{ 
                    maxHeight: '600px', 
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f1f1',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#888',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: '#555',
                      },
                    },
                  }}>
                    {freelancer.freelancerProfileId ? (
                      <FreelancerDetails
                        freelancerProfileId={freelancer.freelancerProfileId}
                        isExpanded={true}
                        showHeader={false}
                        showSocialNetworks={false}
                        showDocuments={false}
                        showAvailability={true}
                        showAdditionalInfo={false}
                        showContact={false}
                        hideLastExchange={true}
                        showNotes={false}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                        {t('project.profileNotAvailable')}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {/* Proposal Tab Content */}
                {(activeTab[freelancer.id] === 1 || activeTab[freelancer.id] === undefined) && activeTab[freelancer.id] !== 0 && (
                  <Box sx={{
                    maxHeight: '600px',
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f1f1',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#888',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: '#555',
                      },
                    },
                  }}>
                    {freelancer.freelancerProfileId ? (
                      <FreelancerProposalDetails
                        projectId={projectId}
                        freelancerProfileId={freelancer.freelancerProfileId}
                        enabled={expandedRows.has(freelancer.id) && activeTab[freelancer.id] === 1}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                        {t('project.proposalNotAvailable')}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
          </TableRow>
        </React.Fragment>
      );
    });
  };

  return (
    <Paper elevation={3} sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" color={zoullooColors.secondary.dark}>
          {t('project.associatedTalents')}
        </Typography>
        {!isClientView && (
          <Stack direction="row" spacing={1}>
            {selectedProposalIds.length > 0 && (
              <Button
                startIcon={bulkTransmitMutation.isPending ? <CircularProgress size={16} /> : <SendIcon />}
                variant="contained"
                onClick={handleBulkTransmit}
                disabled={bulkTransmitMutation.isPending}
                sx={{
                  backgroundColor: zoullooColors.secondary.main,
                  '&:hover': {
                    backgroundColor: zoullooColors.secondary.dark,
                  },
                }}
              >
                {t('project.bulkTransmitToClient')} ({selectedProposalIds.length})
              </Button>
            )}
            {!hasAcceptedProposal && (
              <Button
                startIcon={<PersonAddIcon />}
                variant="outlined"
                onClick={onAddFreelancer}
                sx={{
                  borderColor: zoullooColors.secondary.main,
                  color: zoullooColors.secondary.main,
                  '&:hover': {
                    borderColor: zoullooColors.secondary.dark,
                    backgroundColor: zoullooColors.secondary.alpha,
                  },
                }}
              >
                {t('project.addTalent')}
              </Button>
            )}
          </Stack>
        )}
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {!isClientView && (
                <TableCell padding="checkbox" sx={{ width: 40, pl: 1.5, pr: 0 }}>
                  <Checkbox
                    checked={eligibleFreelancers.length > 0 && selectedProposalIds.length === eligibleFreelancers.length}
                    indeterminate={selectedProposalIds.length > 0 && selectedProposalIds.length < eligibleFreelancers.length}
                    onChange={handleSelectAll}
                    disabled={eligibleFreelancers.length === 0}
                    size="small"
                    sx={{
                      color: zoullooColors.secondary.main,
                      '&.Mui-checked': { color: zoullooColors.secondary.main },
                      '&.MuiCheckbox-indeterminate': { color: zoullooColors.secondary.main },
                    }}
                  />
                </TableCell>
              )}
              <TableCell>{t('project.talentName')}</TableCell>
              <TableCell>{t('project.profile')}</TableCell>
              <TableCell>{t('project.proposalColumn')}</TableCell>
              <TableCell align="right">{t('project.budget')}</TableCell>
              <TableCell align="center">{t('project.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderTableContent()}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ProjectFreelancersTable;